import { useRef, useState, useImperativeHandle, forwardRef, memo } from "react";
import { Avatar, Box, Button, Divider, Flex, HStack, Spacer, Text, useMediaQuery, VStack } from "@chakra-ui/react";
import { MarkdownViewer } from "./markdownviewer";
import { AiOutlineUser } from 'react-icons/ai';
import { VscThumbsdown, VscThumbsup, VscThumbsdownFilled, VscThumbsupFilled } from 'react-icons/vsc';
import { PiCopySimple, PiCopySimpleFill } from "react-icons/pi";
import { CopyToClipboard } from 'react-copy-to-clipboard';
import generatePDF, { Margin, Options, Resolution } from 'react-to-pdf';
import { useTranslation } from 'react-i18next';
import { keyframes } from '@emotion/react';

const typingDot = keyframes`
	  0%, 80%, 100% { transform: scale(0); }
	  40% { transform: scale(1); }
	`;

const Messages = forwardRef(({ messages, messageFeedbackClick, OnMessageRegenerateClick, isEnabled, loginUserName, aiResponseInprogress }:
	{
		messages: any, isEnabled: boolean,
		messageFeedbackClick: (messageId: string, isLiked: Boolean | null, isUpdate: Boolean) => void,
		OnMessageRegenerateClick: (item?: any) => void,
		loginUserName: string
		aiResponseInprogress: boolean
	}, ref) => {
	const { t } = useTranslation();
	const isMobile = useMediaQuery("(max-width: 768px)")[0];
	const targetRef = useRef(null);
	const options: Options = {
		method: "save",
		filename: "chat.pdf",
		resolution: Resolution.MEDIUM,
		page: {
			margin: Margin.MEDIUM,
			format: 'A4',
			orientation: 'portrait',
		},
		canvas: {
			mimeType: 'image/png',
			qualityRatio: 1
		},
		overrides: {
			pdf: {
				compress: true
			},
			canvas: {
				useCORS: true
			}
		},
	};

	useImperativeHandle(ref, () => {
		return {
			async exportPdf() {
				await generatePDF(targetRef, options)
			}
		};
	});

	const messageFeedbackHandler = (item: any, upvote: Boolean) => {
		if (upvote) {
			messageFeedbackClick(item.messageId, item.isLiked == null || item.isLiked === false ? true : null, item.submittedFeedback)
		} else {
			messageFeedbackClick(item.messageId, item.isLiked == null || item.isLiked === true ? false : null, item.submittedFeedback)
		}

	}

	const TypingIndicator = () => {
		return (
			<HStack my="3" spacing="4">
				<Avatar src="/assistant.png" name="Assistant" bg="brand.900" />
				<HStack spacing={1} height="24px">
					{[0, 1, 2].map((i) => (
						<Box
							key={i}
							w="6px"
							h="6px"
							borderRadius="full"
							bg="gray.500"
							animation={`${typingDot} 1.5s ease-in-out ${i * 0.3}s infinite`}
						/>
					))}
				</HStack>
			</HStack>
		);
	};

	const AssistantMessage = ({ index, item, isLast }: { index: number, item: any, isLast: boolean }) => {

		const [isCopied, setCopied] = useState(false);

		const onCopyToClipBoardClick = () => {
			setCopied(true);
			setTimeout(() => {
				setCopied(false)
			}, 1000);
		}

		return <VStack>
			<MarkdownViewer content={item.message} />
			{item.messageId && <>
				<Divider m={0} />
				<Box w={'100%'}>
					<HStack spacing={3}>
						<Spacer />
						{
							!('reg' in item) && (
								<CopyToClipboard text={item.message} onCopy={onCopyToClipBoardClick}>
									{isCopied ? <PiCopySimpleFill color="brand.900" cursor={'pointer'} size={"1.2em"} title="Copied" /> : <PiCopySimple cursor={'pointer'} size={"1.2em"} title="Copy message" />}
								</CopyToClipboard>
							)
						}
						{(isEnabled && !('reg' in item)) && <>
							{
								(item.isLiked == null || item.isLiked === false) && <VscThumbsup title="Like" color="green" size={'1.2em'} cursor={'pointer'} onClick={() => messageFeedbackHandler(item, true)} />
							}
							{
								(item.isLiked != null && item.isLiked === true) && <VscThumbsupFilled title="Liked" color="green" size={'1.2em'} cursor={'pointer'} onClick={() => messageFeedbackHandler(item, true)} />
							}
							{
								(item.isLiked == null || item.isLiked === true) &&

								<>
									<VscThumbsdown title="Dislike" color="red" size={'1.2em'} cursor={'pointer'} onClick={() => messageFeedbackHandler(item, false)} />
									{/*{index === messages.length - 1 && !item.submittedFeedback && <ThumbsDown OnNoClick={() => messageFeedbackHandler(item, false)} OnYesClick={() => OnMessageRegenerateClick(item.messageId)} />}*/}
									{/*{(index === messages.length - 1 || item.submittedFeedback) && <VscThumbsdown title="Dislike" color="red" size={'1.2em'} cursor={'pointer'} onClick={() => messageFeedbackHandler(item, false)} />}*/}
								</>
							}
							{
								item.isLiked != null  && item.isLiked === false && <VscThumbsdownFilled title="Disliked" color="red" size={'1.2em'} cursor={'pointer'} onClick={() => messageFeedbackHandler(item, false)} />
							}
						</>
						}
						{
							('reg' in item && isLast) && (
								<>
									<Button onClick={() => OnMessageRegenerateClick()} color="#2d7150" size="sm" variant="outline">{t('regenerate_no')}</Button>
									<Button onClick={() => OnMessageRegenerateClick(item.messageId)} color="#2d7150" size="sm" variant="outline">{t('regenerate_yes')}</Button>
								</>
							)
						}
					</HStack>
				</Box>
			</>
			}
		</VStack>
	}
	return (
		<Flex w="100%" flexDirection="column" pr={5} ref={targetRef}>
			{messages?.map((item: any, index: number) => {
				if (item.role === "user") {
					return item.message ? (
						<Flex key={index} w="100%" justify="flex-end" borderRadius={5}>
							<Flex
								minW="100px"
								maxW={isMobile ? "85%" : "65%"}
								my="3"
								p={'3'}
								borderRadius={10}
								bg="brand.50" boxShadow='base' rounded='md'
								className="messagebox-right"
							>
								<Text m={0} p={0}>{item.message}</Text>
							</Flex>
							<Avatar ml={-3} bg="brand.900" color={'brand.500'} name={loginUserName ? loginUserName[0].toUpperCase() : ' '} icon={<AiOutlineUser fontSize='1.5rem' name="User" />}></Avatar>
						</Flex>
					) : <></>;
				} else {
					return (
						<Flex key={index} w="100%">

							<Avatar src="/assistant.png"
								name="Assistant"
								bg="brand.900"
							></Avatar>
							<Flex
								minW={0}
								maxW={isMobile ? "85%" : "65%"}
								my="3"
								ml="8"
								p="3"
								borderRadius={10}
								bg="brand.50" boxShadow='base' rounded='md'
								className="messagebox-left"
							>
								<Box w="100%" minW={0} overflowX="hidden">
									<AssistantMessage item={item} index={index} isLast={index === messages.length - 1} />
								</Box>
							</Flex>
						</Flex>
					);
				}
			})}
			{aiResponseInprogress && <TypingIndicator />}
		</Flex>
	);
});

export default memo(Messages);